import { useDataFetchOrchestrator } from "@/hooks/use-data-fetch-orchestrator";
import { useUrlFilters } from "@/hooks/use-url-filters";
import { useSearchPageMapData } from "@/providers/search";
import { Bounds } from "@/providers/search/stores/map-store";
import { Filters } from "@/types/filters";
import { trackEvent } from "@/utils/analytics/track-event";
import { trackPropertySearch } from "@/utils/analytics/track-search";
import { transformFilters } from "@/utils/search/transform-filters";
import { useMap } from "@vis.gl/react-google-maps";
import { useCallback } from "react";
import { UseFormReturn } from "react-hook-form";
import { isDirtyFieldEmpty } from "./shared-utils";

interface UseFilterHandlersProps {
	form: UseFormReturn<Filters>;
	manualDirtyFields: React.MutableRefObject<Record<string, boolean>>;
	onApplyFilters?: (data: Partial<Filters>) => void;
}

export function useFilterHandlers({ form, manualDirtyFields, onApplyFilters }: UseFilterHandlersProps) {
	const { formState } = form;
	const mode = useSearchPageMapData(state => state.mode);
	const fetchMapData = useSearchPageMapData(state => state.fetchMapData);
	const fetchNearByProjects = useSearchPageMapData(state => state.fetchNearByProjects);
	const { orchestrateFetch } = useDataFetchOrchestrator({ mode, fetchMapData, fetchNearByProjects });
	const { filters, setFilters } = useUrlFilters();
	const map = useMap();

	const handleApplyFilters = useCallback(
		(data: Filters) => {
			const filteredValues: Partial<Filters> = { ...filters };

			// Only include fields that are dirty (user has interacted with them)
			(Object.keys(data) as Array<keyof Filters>).forEach(key => {
				const value = data[key];

				if (isDirtyFieldEmpty(key, value)) {
					// When clearing a field, explicitly set it to null to indicate removal
					delete filteredValues[key];
					return;
				}

				// Check if field is dirty (user has interacted with it)
				// Use manual tracking as fallback since React Hook Form dirty detection is broken
				if (!formState.dirtyFields[key] && manualDirtyFields.current[key]) {
					// eslint-disable-next-line no-console
					console.log(`Manual Dirty: Field ${key} is marked as dirty manually`);
				}

				let isDirty =
					formState.dirtyFields[key] ||
					manualDirtyFields.current[key] ||
					(key === "subType" && (formState.dirtyFields.propertyType || manualDirtyFields.current.propertyType));

				// TEMP FIX: If budget has valid values, consider it dirty even if not tracked properly (mobile only)
				if (key === "budget" && typeof value === "object" && value !== null && "min" in value && "max" in value) {
					const budgetValue = value as { min: number; max: number };
					if ((budgetValue.min ?? 0) > 0 || (budgetValue.max ?? 0) > 0) {
						isDirty = true;
					}
				}

				if (!isDirty) {
					return;
				}

				(filteredValues as Record<string, unknown>)[key] = value;
			});

			// Track property search with filters
			trackPropertySearch({ filters: filteredValues });

			// Keep original tracking for compatibility
			trackEvent({
				google: {
					event: "property_search",
				},
				matomo: {
					event: "filter-search",
					options: {
						category: "search",
					},
				},
			});

			const haveLocationsChanged = (() => {
				const currentLocations = filteredValues.locations || [];
				const previousLocations = filters.locations || [];

				// If lengths are different, locations have changed
				if (currentLocations.length !== previousLocations.length) {
					return true;
				}

				// If lengths are the same, check if all locations match by id and name
				const hasChanged = !currentLocations.every(currentLoc =>
					previousLocations.some(
						prevLoc =>
							prevLoc.id === currentLoc.id && prevLoc.name === currentLoc.name && prevLoc.type === currentLoc.type,
					),
				);

				return hasChanged;
			})();

			const mapBounds = map ? (map.getBounds()?.toJSON() as Bounds) : undefined;
			const mapZoom = map ? map.getZoom() : undefined;

			const { filtersForGraphQL, newFilters } = transformFilters({
				filters: filteredValues,
				mapBounds,
				mapZoom,
				haveLocationsChanged,
				from: "filters",
			});
			// console.log("transformFilters: filters case with bounds", newFilters, haveLocationsChanged);
			if (haveLocationsChanged) {
				if (newFilters.zoom) {
					map?.setZoom(newFilters.zoom);
				}
				if (newFilters.coordinates) {
					map?.panTo(newFilters.coordinates);
				}
			}

			setFilters(newFilters);
			orchestrateFetch({ filters: newFilters, filtersForGraphQL, map });
			onApplyFilters?.(newFilters);
		},
		[filters, map, setFilters, orchestrateFetch, onApplyFilters, formState.dirtyFields, manualDirtyFields],
	);

	return {
		handleApplyFilters,
	};
}
